import {ReactNode, useRef} from 'react'
import {Autocomplete, TextField} from '@mui/material'
import {AUTOCOMPLETE_PAPER_PROPS, AUTOCOMPLETE_SX} from './autocompleteSkin'

export type FdsSelectOption<V extends string | number> = {
  value: V
  label: string
  // Optional grouping header (e.g. "Recent" / "All stores").
  group?: string
}

/**
 * Width that fits a label without a fixed guess: ~1ch per character, plus room
 * for the padding and the open/close chevron, clamped so one long name cannot
 * push the toolbar apart.
 */
export const fitWidth = (labels: string[], minCh = 6, maxCh = 28) => {
  const longest = labels.reduce((n, l) => Math.max(n, l.length), 0)
  return `calc(${Math.min(Math.max(longest, minCh), maxCh)}ch + 52px)`
}

interface FdsSelectProps<V extends string | number> {
  options: FdsSelectOption<V>[]
  value: V | null | undefined
  onChange(value: V): void
  // A fixed CSS width, or 'fit' to size to the longest option.
  width?: number | string | 'fit'
  // 'small' for toolbars and table rows; 'medium' matches Bootstrap's floating
  // labels in forms.
  size?: 'small' | 'medium'
  label?: string
  placeholder?: string
  ariaLabel?: string
  disabled?: boolean
  noOptionsText?: ReactNode
  // Minimum width of the open list, when the field is sized to its value
  // rather than to its longest option.
  menuMinWidth?: number
  id?: string
}

/**
 * The scheduler's one dropdown: a type-to-filter MUI Autocomplete wearing the
 * app's Bootstrap colours (autocompleteSkin.ts), so it matches the store picker
 * and follows light/dark mode. Always has a value — it replaces native
 * <select>s, which cannot be cleared either.
 */
function FdsSelect<V extends string | number>({
  options,
  value,
  onChange,
  width = 'fit',
  size = 'small',
  label,
  placeholder,
  ariaLabel,
  disabled,
  noOptionsText = 'No match',
  menuMinWidth,
  id,
}: FdsSelectProps<V>) {
  // The LAST match: a grouped list may repeat a value in a shortcut group
  // ("Recent") ahead of its home in the full list, which is the one to select.
  const match = [...options].reverse().find((o) => o.value === value) || null
  // Callers build their options inline, so the match is a new object on every
  // render. MUI takes a new value object for a changed value and resets the
  // text being typed — a store update mid-search wiped the search. The same
  // option keeps the same object.
  const lastSelected = useRef(match)
  const prev = lastSelected.current
  const selected =
    match &&
    prev &&
    match.value === prev.value &&
    match.label === prev.label &&
    match.group === prev.group
      ? prev
      : match
  lastSelected.current = selected
  const resolvedWidth =
    width === 'fit' ? fitWidth(options.map((o) => o.label).concat(label ? [label] : [])) : width

  return (
    <Autocomplete
      id={id}
      size={size}
      options={options}
      groupBy={options.some((o) => o.group) ? (o) => o.group || '' : undefined}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(o, v) => o.value === v.value && o.group === v.group}
      renderOption={(props, o) => (
        <li {...props} key={`${o.group || ''}-${o.value}`}>
          {o.label}
        </li>
      )}
      value={selected}
      disableClearable={!!selected}
      blurOnSelect
      autoHighlight
      disabled={disabled}
      noOptionsText={noOptionsText}
      onChange={(_e, o) => {
        if (o && o.value !== value) onChange(o.value)
      }}
      sx={{...AUTOCOMPLETE_SX, width: resolvedWidth}}
      ListboxProps={{style: {maxHeight: 320}}}
      componentsProps={{
        ...AUTOCOMPLETE_PAPER_PROPS,
        ...(menuMinWidth
          ? {popper: {placement: 'bottom-start', style: {minWidth: menuMinWidth}}}
          : {}),
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          size={size}
          inputProps={{...params.inputProps, 'aria-label': ariaLabel || label}}
        />
      )}
    />
  )
}

export {FdsSelect}
